import React from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { cssMap } from '@atlaskit/css';
import DatePicker from '@atlaskit/datetime-picker/date-picker';
import TimePicker from '@atlaskit/datetime-picker/time-picker';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';

import { asFormDateTimeField } from '../../../../../../../../common/types/form/form-fields/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { useFormField } from '../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';

const styles = cssMap({
	container: {
		maxWidth: '320px',
	},
	pickerContainer: {
		width: '50%',
	},
});

export const PreviewDateTimeInput = ({
	fieldId,
	fieldInputId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useFormField);

	const intl = useIntl();

	const formField = useFormField({ fieldId });
	const formDateTimeField = asFormDateTimeField(formField);

	if (formDateTimeField === undefined) {
		return <></>;
	}

	// NOTE: See edit component for why we are using the individual `DatePicker` and `TimePicker`.
	return (
		<Inline xcss={styles.container}>
			<Box xcss={styles.pickerContainer}>
				<DatePicker
					id={fieldInputId}
					value={formDateTimeField.value?.date}
					locale={intl.locale}
					shouldShowCalendarButton
					testId={`field-input-date-time-date-picker-edit-${fieldId}`}
					isDisabled
					label={
						formField?.label
							? `${formField.label} ${intl.formatMessage(messages.timeLabelSuffix)}`
							: intl.formatMessage(messages.dateLabel)
					}
				/>
			</Box>
			<Box xcss={styles.pickerContainer}>
				<TimePicker
					id={fieldInputId} // Duplicate `id` from above to resolve a11y.
					value={formDateTimeField.value?.localTime}
					locale={intl.locale}
					testId={`field-input-date-time-time-picker-edit-${fieldId}`}
					isDisabled
					label={
						formField?.label
							? `${formField.label} ${intl.formatMessage(messages.dateTimeLabelSuffix)}`
							: intl.formatMessage(messages.timeLabel)
					}
				/>
			</Box>
		</Inline>
	);
};
